/**
 * Show Sync (The Notes List integration) — presentation helpers.
 *
 * Show Sync is the cross-app feature where a project's script structure feeds
 * The Notes List. A project that is actively syncing gets a small teal marker
 * on its dashboard card so it is distinguishable at a glance.
 *
 * WHY A MIXIN
 * The "is this project synced" test must stay identical to
 * `staging-v2-main`'s `isNotesListLinked`, or the two builds will disagree
 * about what "synced" means once they merge. Defining it once here means the
 * grid tile and the list row cannot drift from each other either.
 *
 * DELIBERATELY NOT GATED ON THE VIEWER'S SUBSCRIPTION.
 * Show Sync is a property of the *project*, not of whoever is looking at it.
 * If a project is syncing, it is syncing for every collaborator, so hiding the
 * marker from collaborators without a dual subscription would mean two people
 * on the same project see different things — and leave the collaborator with
 * no explanation for why their structure appears in another app. The marker
 * never appears on unsynced projects, so there is no upsell noise to suppress.
 * Revisit only if a client-side entitlement signal is ever added; today none
 * exists on either branch (the dual/entitlement fields live in `functions/`).
 */
export default {
    computed: {
        hasActiveShowSync() {
            const p = this.project;
            // Matches staging-v2-main's isNotesListLinked exactly. Both halves
            // matter: a project can carry a stale 'linked' status with no
            // production id, which is not a live sync.
            return !!(p
                && p.noteslist_link_status === 'linked'
                && p.noteslist_production_id);
        },
        // "Linked but the last sync failed" is a distinct state from "not
        // linked". The project is still connected and will retry, so the marker
        // stays on the card — it just changes colour and wording rather than
        // disappearing, which would read as "sync was turned off".
        showSyncFailed() {
            const p = this.project;
            return !!(this.hasActiveShowSync && p && p.noteslist_last_sync_error);
        },
        // Owner, or a collaborator the owner made an administrator. Same test as
        // project-settings/nav.vue's manage gate, so the wording here cannot
        // promise an action the settings screen then refuses.
        canManageShowSync() {
            const p = this.project;
            const u = this.user;
            if (!p || !u || !u.uid) {
                // Unknown viewer: say nothing about who can act, rather than
                // telling a collaborator they can do something they cannot.
                return true;
            }
            if (p.ownerUID === u.uid) {
                return true;
            }
            const collab = (p.collaborators || {})[u.uid];
            return !!(collab && collab.type === 'administrator');
        },
        showSyncTooltip() {
            if (this.showSyncFailed) {
                return this.canManageShowSync
                    ? 'Show Sync error — the last sync with The Notes List did not complete'
                    : 'Show Sync error — the last sync with The Notes List did not complete. '
                      + 'The project owner can resolve this.';
            }
            // A collaborator sees the marker (see the note above on why it is not
            // hidden from them) but cannot connect or disconnect the project, so
            // the neutral wording read as an offer they could not take up.
            return this.canManageShowSync
                ? 'Show Sync is active — this project’s structure syncs with The Notes List'
                : 'Show Sync is active — this project’s structure syncs with The Notes List. '
                  + 'Only the project owner can change this.';
        },
        // Colour alone should not be the only signal, so the tooltip and
        // aria-label carry the difference for anyone who cannot distinguish
        // teal from red.
        showSyncStateClass() {
            return this.showSyncFailed ? 'is-failed' : 'is-active';
        }
    }
};


// WEBPACK FOOTER //
// ./src/mixins/showSync.js